import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Chip,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Alert,
  Switch,
  FormControlLabel,
  Snackbar,
  SelectChangeEvent,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PlaylistAddRounded as PlaylistAddIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  TuneRounded as TuneIcon,
} from '@mui/icons-material';
import {
  useAdminCommissions,
  useAdminCommission,
  Commission,
  CommissionFilters,
  PROVIDERS,
  ProviderOption,
  useProviders,
  useServiceTypes,
} from '../../hooks/admin/useAdminCommissions';
import CommissionForm from '../../components/admin/CommissionForm';
import CommissionView from '../../components/admin/CommissionView';
import BulkCommissionAdd from '../../components/admin/BulkCommissionAdd';
import {
  INK,
  FONTS,
  primaryBtnSx,
  ghostBtnSx,
  dangerBtnSx,
  editorialDialogTitleSx,
  ledgerInputSx,
  ledgerChipSx,
} from '../../styles/ledger';

// Tab panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`commission-tabpanel-${index}`}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const CommissionsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [userIdFilter, setUserIdFilter] = useState<number | null>(null); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [providerFilters, setProviderFilters] = useState<string[]>([]);
  const [serviceTypeFilters, setServiceTypeFilters] = useState<string[]>([]);
  const [serviceCodeFilter, setServiceCodeFilter] = useState<string>('all'); // eslint-disable-line @typescript-eslint/no-unused-vars
  const [showGeneralOnly, setShowGeneralOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCommissionId, setSelectedCommissionId] = useState<number | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [isBulkAddDialogOpen, setIsBulkAddDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');

  const { data: providerOptions = PROVIDERS } = useProviders();
  const { data: serviceTypes = {} } = useServiceTypes(providerFilters);

  const filters: CommissionFilters = {
    user_ids: userIdFilter ? [userIdFilter] : undefined,
    providers: providerFilters.length > 0 ? providerFilters : undefined,
    service_types: serviceTypeFilters.length > 0 ? serviceTypeFilters : undefined,
    is_general: showGeneralOnly || undefined,
    is_active: tabValue === 1 ? true : tabValue === 2 ? false : undefined,
    page: page + 1,
    per_page: rowsPerPage,
  };

  const {
    commissions,
    pagination,
    totalCommissions, // eslint-disable-line @typescript-eslint/no-unused-vars
    isLoading,
    error,
    refetch,
    createCommission,
    updateCommission,
    deleteCommission,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAdminCommissions(filters);

  const {
    commission: selectedCommission,
    isLoading: isLoadingCommission,
    error: commissionError,
    updateCommission: updateSelectedCommission, // eslint-disable-line @typescript-eslint/no-unused-vars
  } = useAdminCommission(selectedCommissionId ?? 0);

  // Handlers — unchanged behavior
  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };
  const handleChangePage = (_e: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleServiceCodeFilterChange = (e: SelectChangeEvent<string>) => {
    setServiceCodeFilter(e.target.value);
    setPage(0);
  };
  const handleGeneralFilterToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowGeneralOnly(e.target.checked);
    setPage(0);
  };
  const handleViewCommission = (id: number) => {
    setSelectedCommissionId(id);
    setViewMode(true);
  };

  const handleBulkAddCompleted = (created: number, failed: number) => {
    setSnackbarMessage(
      failed > 0
        ? `Bulk add: ${created} created, ${failed} failed — failed rows kept so you can fix and retry`
        : `${created} ${created === 1 ? 'commission' : 'commissions'} created`
    );
    setSnackbarSeverity(failed > 0 ? 'info' : 'success');
    setSnackbarOpen(true);
    refetch();
    if (failed === 0) setIsBulkAddDialogOpen(false);
  };

  const handleCreateCommission = async (data: any) => {
    try {
      await createCommission(data);
      setIsCreateDialogOpen(false);
      setSnackbarMessage('Commission created successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetch();
    } catch (err) {
      console.error('Error creating commission:', err);
      setSnackbarMessage('Failed to create commission');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleEditCommission = async (data: any) => {
    if (!selectedCommissionId) return;
    try {
      await updateCommission(selectedCommissionId, data);
      setIsEditDialogOpen(false);
      setSnackbarMessage('Commission updated successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetch();
      if (viewMode) {
        setTimeout(() => {
          setSelectedCommissionId(null);
          setSelectedCommissionId(selectedCommissionId);
        }, 300);
      }
    } catch (err) {
      console.error('Error updating commission:', err);
      setSnackbarMessage('Failed to update commission');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloneCommission = async (data: any) => {
    try {
      await createCommission(data);
      setIsCloneDialogOpen(false);
      setSelectedCommissionId(null);
      setSnackbarMessage('Commission cloned successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      refetch();
    } catch (err) {
      console.error('Error cloning commission:', err);
      setSnackbarMessage('Failed to clone commission');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteCommission = async () => {
    if (!selectedCommissionId) return;
    try {
      await deleteCommission(selectedCommissionId);
      setIsDeleteDialogOpen(false);
      setSnackbarMessage('Commission deleted successfully');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      if (viewMode) setViewMode(false);
      setSelectedCommissionId(null);
      refetch();
    } catch (err) {
      console.error('Error deleting commission:', err);
      setSnackbarMessage('Failed to delete commission');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  // Statistics — now with meaningful aggregates
  const stats = useMemo(() => {
    const activeCount = commissions.filter((c) => c.is_active).length;
    const tieredCount = commissions.filter((c) => c.commission_tiers && c.commission_tiers.length > 0).length;
    const flatCommissions = commissions.filter((c) => c.value_amount !== undefined && c.value_amount !== null);
    const percentageRates = flatCommissions
      .filter((c) => c.value_type === 'percentage')
      .map((c) => Number(c.value_amount) || 0);
    const avgPercentage =
      percentageRates.length > 0
        ? percentageRates.reduce((s, v) => s + v, 0) / percentageRates.length
        : 0;
    const avgPriority =
      commissions.length > 0
        ? commissions.reduce((s, c) => s + c.priority, 0) / commissions.length
        : 0;

    return {
      total: commissions.length,
      active: activeCount,
      inactive: commissions.length - activeCount,
      tiered: tieredCount,
      avgPercentage: avgPercentage ? Number(avgPercentage.toFixed(1)) : 0,
      avgPriority: avgPriority ? Number(avgPriority.toFixed(1)) : 0,
    };
  }, [commissions]);

  // Preserved view-mode branch
  if (viewMode) {
    return (
      <Box sx={{ fontFamily: FONTS.sans }}>
        <CommissionView
          commission={selectedCommission}
          isLoading={isLoadingCommission}
          error={commissionError}
          onEdit={() => setIsEditDialogOpen(true)}
          onDelete={() => setIsDeleteDialogOpen(true)}
          onBack={() => {
            setViewMode(false);
            setSelectedCommissionId(null);
          }}
          isDeleting={isDeleting}
        />
        <Dialog open={isEditDialogOpen} onClose={() => setIsEditDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
          <DialogTitle sx={editorialDialogTitleSx}>Edit commission</DialogTitle>
          <DialogContent dividers>
            <CommissionForm initialData={selectedCommission} onSubmit={handleEditCommission} isSubmitting={isUpdating} isEditMode />
          </DialogContent>
        </Dialog>
        <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.5 } }}>
          <DialogTitle sx={editorialDialogTitleSx}>Delete commission</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 0, border: `1px solid ${INK.warning}`, bgcolor: INK.amberSoft }}>
              This action cannot be undone.
            </Alert>
            <Typography sx={{ fontFamily: FONTS.sans, color: INK.muted }}>
              All associated configuration for this commission will be permanently removed.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={() => setIsDeleteDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
            <Button onClick={handleDeleteCommission} disabled={isDeleting} sx={dangerBtnSx} startIcon={isDeleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}>
              {isDeleting ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        fontFamily: FONTS.sans,
        bgcolor: INK.bg,
        color: INK.text,
        mx: { xs: -2, md: -3 },
        my: { xs: -2, md: -3 },
        px: { xs: 3, md: 6 },
        py: { xs: 4, md: 5 },
        minHeight: 'calc(100vh - 64px)',
        position: 'relative',
        // Subtle paper grain
        backgroundImage:
          'radial-gradient(ellipse at top left, rgba(31,78,61,0.025), transparent 50%), radial-gradient(ellipse at bottom right, rgba(184,101,27,0.02), transparent 50%)',
      }}
    >
      {/* ─── HEADER ─── */}
      <Box
        sx={{
          mb: { xs: 4, md: 6 },
          animation: 'fadeUp 0.6s ease-out',
          '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(8px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{ width: 24, height: 1, bgcolor: INK.accent }} />
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 11,
              letterSpacing: '0.18em',
              color: INK.accent,
              fontWeight: 500,
              textTransform: 'uppercase',
            }}
          >
            Admin · Ledger No. 04
          </Typography>
        </Stack>

        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'flex-end' }} justifyContent="space-between" spacing={3}>
          <Box sx={{ maxWidth: 720 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONTS.serif,
                fontSize: { xs: 44, md: 64 },
                lineHeight: 0.95,
                letterSpacing: '-0.02em',
                color: INK.text,
                mb: 2,
                fontWeight: 400,
              }}
            >
              Commission
              <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
                {' '}management.
              </Box>
            </Typography>
            <Typography
              sx={{
                fontFamily: FONTS.sans,
                fontSize: 15,
                lineHeight: 1.55,
                color: INK.muted,
                maxWidth: 560,
              }}
            >
              Structures, overrides, and tiered rates across users, providers, and shipping corridors —
              maintained as a single source of truth.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                sx={{
                  border: `1px solid ${INK.hairline}`,
                  borderRadius: 0.5,
                  width: 40,
                  height: 40,
                  color: INK.muted,
                  '&:hover': { bgcolor: INK.surfaceTint, borderColor: INK.text, color: INK.text },
                }}
                size="small"
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <Button
              onClick={() => setIsBulkAddDialogOpen(true)}
              startIcon={<PlaylistAddIcon sx={{ fontSize: 18 }} />}
              sx={ghostBtnSx}
            >
              Bulk add
            </Button>
            <Button
              onClick={() => setIsCreateDialogOpen(true)}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              sx={primaryBtnSx}
            >
              New commission
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* ─── KPI STRIP ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          mb: 4,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          animation: 'fadeUp 0.7s ease-out',
        }}
      >
        <KPI label="Total commissions" value={stats.total} sub={`${stats.tiered} tier-based`} />
        <KPI label="Active" value={stats.active} sub={`${stats.inactive} paused`} accent={stats.active > 0} />
        <KPI label="Average rate" value={stats.avgPercentage ? `${stats.avgPercentage}%` : '—'} sub="across % types" />
        <KPI label="Avg priority" value={stats.avgPriority || '—'} sub="lower fires first" isLast />
      </Box>

      {/* ─── FILTER BAR ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          p: 2.5,
          mb: 3,
          animation: 'fadeUp 0.8s ease-out',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <TuneIcon sx={{ fontSize: 16, color: INK.accent }} />
          <Typography
            sx={{
              fontFamily: FONTS.mono,
              fontSize: 11,
              letterSpacing: '0.14em',
              color: INK.muted,
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            Filters
          </Typography>
          <Box sx={{ flex: 1, height: 1, bgcolor: INK.hairlineSoft }} />
        </Stack>

        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={1.5}
          alignItems={{ lg: 'center' }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Autocomplete
              multiple
              size="small"
              options={providerOptions}
              getOptionLabel={(o) => o.label}
              value={providerOptions.filter((p) => providerFilters.includes(p.value))}
              onChange={(_, v) => {
                setProviderFilters(v.map((x) => x.value));
                setServiceTypeFilters([]);
              }}
              renderInput={(params) => (
                <TextField {...params} placeholder="All providers" sx={ledgerInputSx} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, i) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index: i })}
                    size="small"
                    sx={ledgerChipSx}
                  />
                ))
              }
            />
          </Box>

          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Autocomplete
              multiple
              size="small"
              disabled={providerFilters.length === 0}
              options={Object.entries(serviceTypes).flatMap(([category, services]) =>
                Object.entries(services as Record<string, string>).map(([code, name]) => ({
                  value: code,
                  label: name,
                  category,
                }))
              )}
              groupBy={(o) => o.category.toUpperCase()}
              getOptionLabel={(o) => o.label}
              value={Object.entries(serviceTypes)
                .flatMap(([category, services]) =>
                  Object.entries(services as Record<string, string>).map(([code, name]) => ({
                    value: code,
                    label: name,
                    category,
                  }))
                )
                .filter((st) => serviceTypeFilters.includes(st.value))}
              onChange={(_, v) => setServiceTypeFilters(v.map((x) => x.value))}
              renderInput={(params) => (
                <TextField {...params} placeholder="All service types" sx={ledgerInputSx} />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, i) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index: i })}
                    size="small"
                    sx={ledgerChipSx}
                  />
                ))
              }
            />
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={showGeneralOnly}
                onChange={handleGeneralFilterToggle}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: INK.accent },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: INK.accent },
                }}
              />
            }
            label={
              <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.text }}>
                General only
              </Typography>
            }
            sx={{ ml: 0, mr: 0 }}
          />
        </Stack>
      </Box>

      {/* ─── TABLE CARD ─── */}
      <Box
        sx={{
          border: `1px solid ${INK.hairline}`,
          bgcolor: INK.surface,
          borderRadius: 0.5,
          overflow: 'hidden',
          animation: 'fadeUp 0.9s ease-out',
        }}
      >
        {/* Tab strip */}
        <Box sx={{ borderBottom: `1px solid ${INK.hairline}`, px: 2.5 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              minHeight: 52,
              '& .MuiTabs-indicator': { bgcolor: INK.accent, height: 2 },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontFamily: FONTS.sans,
                fontWeight: 500,
                fontSize: 14,
                color: INK.subtle,
                minHeight: 52,
                px: 1.5,
                mr: 1,
                letterSpacing: '-0.005em',
                '&.Mui-selected': { color: INK.text, fontWeight: 600 },
              },
            }}
          >
            <Tab label={<TabLabel label="All commissions" count={stats.total} active={tabValue === 0} />} />
            <Tab label={<TabLabel label="Active" count={stats.active} active={tabValue === 1} />} />
            <Tab label={<TabLabel label="Inactive" count={stats.inactive} active={tabValue === 2} />} />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <CommissionsTable
            commissions={commissions}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalCount={pagination?.total || 0}
            isLoading={isLoading}
            error={error}
            providerOptions={providerOptions}
            onView={handleViewCommission}
            onEdit={(id) => { setSelectedCommissionId(id); setIsEditDialogOpen(true); }}
            onClone={(id) => { setSelectedCommissionId(id); setIsCloneDialogOpen(true); }}
            onDelete={(id) => { setSelectedCommissionId(id); setIsDeleteDialogOpen(true); }}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <CommissionsTable
            commissions={commissions}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalCount={pagination?.total || 0}
            isLoading={isLoading}
            error={error}
            providerOptions={providerOptions}
            onView={handleViewCommission}
            onEdit={(id) => { setSelectedCommissionId(id); setIsEditDialogOpen(true); }}
            onClone={(id) => { setSelectedCommissionId(id); setIsCloneDialogOpen(true); }}
            onDelete={(id) => { setSelectedCommissionId(id); setIsDeleteDialogOpen(true); }}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <CommissionsTable
            commissions={commissions}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalCount={pagination?.total || 0}
            isLoading={isLoading}
            error={error}
            providerOptions={providerOptions}
            onView={handleViewCommission}
            onEdit={(id) => { setSelectedCommissionId(id); setIsEditDialogOpen(true); }}
            onClone={(id) => { setSelectedCommissionId(id); setIsCloneDialogOpen(true); }}
            onDelete={(id) => { setSelectedCommissionId(id); setIsDeleteDialogOpen(true); }}
          />
        </TabPanel>
      </Box>

      {/* Editorial footer mark */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 4, opacity: 0.6 }}>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.18em', color: INK.subtle, textTransform: 'uppercase' }}>
          Almaship · Internal ledger · {new Date().getFullYear()}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.subtle }}>
          {String(pagination?.total ?? 0).padStart(4, '0')} records
        </Typography>
      </Stack>

      {/* ─── DIALOGS — preserved ─── */}
      <Dialog open={isCreateDialogOpen} onClose={() => setIsCreateDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>New commission</DialogTitle>
        <DialogContent dividers>
          <CommissionForm onSubmit={handleCreateCommission} isSubmitting={isCreating} />
        </DialogContent>
      </Dialog>

      <Dialog open={isCloneDialogOpen} onClose={() => { setIsCloneDialogOpen(false); setSelectedCommissionId(null); }} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>
          Clone commission
          <Typography sx={{ display: 'block', fontFamily: FONTS.sans, fontSize: 12, color: INK.muted, fontWeight: 400, mt: 0.5 }}>
            Creates a new commission based on the selected configuration.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <CommissionForm initialData={selectedCommission} onSubmit={handleCloneCommission} isSubmitting={isCreating} />
        </DialogContent>
      </Dialog>

      <Dialog open={isEditDialogOpen && !viewMode} onClose={() => setIsEditDialogOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Edit commission</DialogTitle>
        <DialogContent dividers>
          <CommissionForm initialData={selectedCommission} onSubmit={handleEditCommission} isSubmitting={isUpdating} isEditMode />
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen && !viewMode} onClose={() => setIsDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Delete commission</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 0, border: `1px solid ${INK.warning}`, bgcolor: INK.amberSoft }}>
            This action cannot be undone.
          </Alert>
          <Typography sx={{ fontFamily: FONTS.sans, color: INK.muted }}>
            All associated configuration for this commission will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsDeleteDialogOpen(false)} sx={ghostBtnSx}>Cancel</Button>
          <Button onClick={handleDeleteCommission} disabled={isDeleting} sx={dangerBtnSx} startIcon={isDeleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isBulkAddDialogOpen} onClose={() => setIsBulkAddDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 0.5 } }}>
        <DialogTitle sx={editorialDialogTitleSx}>Bulk add commissions</DialogTitle>
        <DialogContent dividers>
          <BulkCommissionAdd createCommission={createCommission} onCompleted={handleBulkAddCompleted} />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsBulkAddDialogOpen(false)} sx={ghostBtnSx}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: 0, fontFamily: FONTS.sans }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

/* ─────────────────────────────────────────────
   KPI cell — typographic, no boxes
   ───────────────────────────────────────────── */
const KPI: React.FC<{
  label: string;
  value: number | string;
  sub: string;
  accent?: boolean;
  isLast?: boolean;
}> = ({ label, value, sub, accent, isLast }) => (
  <Box
    sx={{
      px: { xs: 2.5, md: 4 },
      py: { xs: 3, md: 3.5 },
      borderRight: { md: isLast ? 'none' : `1px solid ${INK.hairline}` },
      borderBottom: { xs: isLast ? 'none' : `1px solid ${INK.hairline}`, md: 'none' },
      position: 'relative',
    }}
  >
    <Typography
      sx={{
        fontFamily: FONTS.mono,
        fontSize: 10,
        letterSpacing: '0.16em',
        color: INK.subtle,
        textTransform: 'uppercase',
        mb: 1.5,
        fontWeight: 500,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONTS.serif,
        fontSize: { xs: 40, md: 52 },
        lineHeight: 0.9,
        color: accent ? INK.accent : INK.text,
        letterSpacing: '-0.02em',
        mb: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: FONTS.sans,
        fontSize: 12,
        color: INK.subtle,
        letterSpacing: '-0.005em',
      }}
    >
      {sub}
    </Typography>
  </Box>
);

/* ─────────────────────────────────────────────
   Tab label
   ───────────────────────────────────────────── */
const TabLabel: React.FC<{ label: string; count: number; active: boolean }> = ({ label, count, active }) => (
  <Stack direction="row" alignItems="center" spacing={1.2}>
    <span>{label}</span>
    <Box
      component="span"
      sx={{
        fontFamily: FONTS.mono,
        fontSize: 10,
        bgcolor: active ? INK.accent : INK.hairlineSoft,
        color: active ? INK.surface : INK.muted,
        px: 0.85,
        py: 0.3,
        borderRadius: 0.5,
        letterSpacing: '0.02em',
        fontWeight: 500,
        minWidth: 20,
        textAlign: 'center',
      }}
    >
      {String(count).padStart(2, '0')}
    </Box>
  </Stack>
);

/* ─────────────────────────────────────────────
   COMMISSIONS TABLE
   ───────────────────────────────────────────── */
interface CommissionsTableProps {
  commissions: Commission[];
  page: number;
  rowsPerPage: number;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
  providerOptions: ProviderOption[];
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onClone: (id: number) => void;
  onDelete: (id: number) => void;
}

const CommissionsTable: React.FC<CommissionsTableProps> = ({
  commissions,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  totalCount,
  isLoading,
  error,
  providerOptions,
  onView,
  onEdit,
  onClone,
  onDelete,
}) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress size={28} sx={{ color: INK.accent }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans }}>
          <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Error loading commissions</Typography>
          <Typography sx={{ fontSize: 13 }}>{error.message}</Typography>
        </Alert>
      </Box>
    );
  }

  const getZoneDisplay = (zone: any, zoneType: string | null | undefined) => {
    if (!zone || !zoneType) return 'Any zone';
    return zone.name || zone.code || '—';
  };

  const zonePrefix = (type: string | null | undefined) => {
    switch (type) {
      case 'country': return 'CTRY';
      case 'state': return 'ST';
      case 'city': return 'CITY';
      default: return 'ZN';
    }
  };

  /** Format the rate cell — handles flat AND tier-based commissions */
  const renderRate = (c: Commission) => {
    // Tier-based commission
    if (c.commission_tiers && c.commission_tiers.length > 0) {
      const tiers = c.commission_tiers;
      const allPercent = tiers.every((t) => t.value_type === 'percentage');
      const values = tiers.map((t) => Number(t.value_amount) || 0);
      const min = Math.min(...values);
      const max = Math.max(...values);
      const suffix = allPercent ? '%' : '';
      const display = min === max ? `${min}${suffix}` : `${min}–${max}${suffix}`;
      return (
        <Stack spacing={0.5}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 14, fontWeight: 600, color: INK.text, letterSpacing: '-0.01em' }}>
            {display}
          </Typography>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.subtle, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Tiered · {tiers.length}
          </Typography>
        </Stack>
      );
    }
    // Flat commission
    if (c.value_amount !== undefined && c.value_amount !== null) {
      const isPct = c.value_type === 'percentage';
      return (
        <Stack spacing={0.5}>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 14, fontWeight: 600, color: INK.text, letterSpacing: '-0.01em' }}>
            {c.value_amount}{isPct ? '%' : ''}
          </Typography>
          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.subtle, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            {isPct ? 'Percentage' : 'Fixed · USD'}
          </Typography>
        </Stack>
      );
    }
    return (
      <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.faint }}>—</Typography>
    );
  };

  return (
    <>
      <TableContainer>
        <Table sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  fontFamily: FONTS.mono,
                  fontSize: 10,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: INK.subtle,
                  fontWeight: 500,
                  borderBottom: `1px solid ${INK.hairline}`,
                  bgcolor: INK.surfaceTint,
                  py: 1.5,
                },
              }}
            >
              <TableCell sx={{ width: 56 }}>№</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Channel</TableCell>
              <TableCell>Corridor</TableCell>
              <TableCell sx={{ minWidth: 110 }}>Rate</TableCell>
              <TableCell sx={{ width: 80 }}>Prio.</TableCell>
              <TableCell sx={{ width: 100 }}>Status</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {commissions.length > 0 ? (
              commissions.map((commission, idx) => (
                <TableRow
                  key={commission.id}
                  sx={{
                    '& .MuiTableCell-root': {
                      borderBottom: `1px solid ${INK.hairlineSoft}`,
                      fontFamily: FONTS.sans,
                      py: 2,
                      verticalAlign: 'top',
                    },
                    position: 'relative',
                    transition: 'background-color 0.15s ease',
                    '&:hover': { bgcolor: INK.surfaceTint },
                    '&:hover .row-actions': { opacity: 1 },
                    '&:hover td:first-of-type::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: 2,
                      bgcolor: INK.accent,
                    },
                    animation: `rowIn 0.3s ${idx * 30}ms ease-out backwards`,
                    '@keyframes rowIn': { from: { opacity: 0, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
                  }}
                >
                  {/* № */}
                  <TableCell>
                    <Typography
                      sx={{
                        fontFamily: FONTS.mono,
                        fontSize: 13,
                        color: INK.text,
                        fontWeight: 500,
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {String(commission.id).padStart(3, '0')}
                    </Typography>
                  </TableCell>

                  {/* Created */}
                  <TableCell>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
                      {new Date(commission.created_at).toLocaleDateString('en-CA')}
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.faint, letterSpacing: '0.1em', mt: 0.25 }}>
                      {new Date(commission.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </TableCell>

                  {/* Scope */}
                  <TableCell>
                    {commission.users && commission.users.length > 0 ? (
                      <Stack spacing={0.5}>
                        {commission.users.slice(0, 1).map((user) => (
                          <Box key={user.id}>
                            <Typography sx={{ fontSize: 13.5, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                              {user.first_name} {user.last_name}
                            </Typography>
                            <Typography sx={{ fontSize: 11.5, color: INK.subtle, fontFamily: FONTS.mono }}>
                              {user.email}
                            </Typography>
                          </Box>
                        ))}
                        {commission.users.length > 1 && (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.accent, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                            + {commission.users.length - 1} more
                          </Typography>
                        )}
                      </Stack>
                    ) : (
                      <Typography
                        sx={{
                          display: 'inline-block',
                          fontFamily: FONTS.mono,
                          fontSize: 10.5,
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                          color: INK.accent,
                          border: `1px solid ${INK.accent}`,
                          bgcolor: INK.accentSoft,
                          px: 1,
                          py: 0.4,
                          fontWeight: 500,
                        }}
                      >
                        General
                      </Typography>
                    )}
                  </TableCell>

                  {/* Channel: provider + service inline */}
                  <TableCell>
                    <Stack spacing={0.8}>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {commission.providers && commission.providers.length > 0 ? (
                          commission.providers.map((p: string) => (
                            <Box
                              key={p}
                              sx={{
                                fontFamily: FONTS.mono,
                                fontSize: 10.5,
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                color: INK.text,
                                border: `1px solid ${INK.hairline}`,
                                px: 0.8,
                                py: 0.3,
                                fontWeight: 600,
                                bgcolor: INK.surface,
                              }}
                            >
                              {providerOptions.find((x) => x.value === p)?.label || p}
                            </Box>
                          ))
                        ) : (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>All providers</Typography>
                        )}
                      </Stack>
                      {commission.service_types && commission.service_types.length > 0 ? (
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          {commission.service_types.slice(0, 2).map((st) => (
                            <Typography
                              key={st}
                              sx={{
                                fontFamily: FONTS.mono,
                                fontSize: 10,
                                color: INK.muted,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                              }}
                            >
                              {st.replace(/_/g, ' ').toLowerCase()}
                            </Typography>
                          ))}
                          {commission.service_types.length > 2 && (
                            <Typography
                              sx={{
                                fontFamily: FONTS.mono,
                                fontSize: 10,
                                color: INK.accent,
                                letterSpacing: '0.06em',
                              }}
                            >
                              +{commission.service_types.length - 2}
                            </Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 10, color: INK.faint }}>any service</Typography>
                      )}
                    </Stack>
                  </TableCell>

                  {/* Corridor: origin → destination */}
                  <TableCell>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {commission.origin_zone_details && commission.origin_zone_details.length > 0 ? (
                          <Stack spacing={0.3}>
                            {commission.origin_zone_details.slice(0, 1).map((z, i) => (
                              <Box key={i}>
                                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.faint, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                                  {zonePrefix(z.type)}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                                  {getZoneDisplay(z.data, z.type)}
                                </Typography>
                              </Box>
                            ))}
                            {commission.origin_zone_details.length > 1 && (
                              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.accent }}>
                                +{commission.origin_zone_details.length - 1}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.subtle, letterSpacing: '0.04em' }}>
                            Any
                          </Typography>
                        )}
                      </Box>
                      <ArrowForwardIcon sx={{ fontSize: 14, color: INK.faint }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        {commission.destination_zone_details && commission.destination_zone_details.length > 0 ? (
                          <Stack spacing={0.3}>
                            {commission.destination_zone_details.slice(0, 1).map((z, i) => (
                              <Box key={i}>
                                <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.faint, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                                  {zonePrefix(z.type)}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                                  {getZoneDisplay(z.data, z.type)}
                                </Typography>
                              </Box>
                            ))}
                            {commission.destination_zone_details.length > 1 && (
                              <Typography sx={{ fontFamily: FONTS.mono, fontSize: 9.5, color: INK.accent }}>
                                +{commission.destination_zone_details.length - 1}
                              </Typography>
                            )}
                          </Stack>
                        ) : (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11.5, color: INK.subtle, letterSpacing: '0.04em' }}>
                            Any
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Rate */}
                  <TableCell>{renderRate(commission)}</TableCell>

                  {/* Priority */}
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 36,
                        height: 28,
                        px: 1,
                        bgcolor: INK.surfaceTint,
                        border: `1px solid ${INK.hairline}`,
                        fontFamily: FONTS.mono,
                        fontSize: 12,
                        color: INK.text,
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {commission.priority}
                    </Box>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box
                        sx={{
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: commission.is_active ? INK.active : INK.inactive,
                          boxShadow: commission.is_active ? `0 0 0 3px ${INK.accentSoft}` : 'none',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: FONTS.mono,
                          fontSize: 10.5,
                          letterSpacing: '0.14em',
                          textTransform: 'uppercase',
                          color: commission.is_active ? INK.active : INK.inactive,
                          fontWeight: 500,
                        }}
                      >
                        {commission.is_active ? 'Active' : 'Paused'}
                      </Typography>
                    </Stack>
                  </TableCell>

                  {/* Actions */}
                  <TableCell align="right">
                    <Stack
                      className="row-actions"
                      direction="row"
                      spacing={0.5}
                      justifyContent="flex-end"
                      sx={{ opacity: 0.45, transition: 'opacity 0.15s' }}
                    >
                      <ActionIcon title="View" onClick={() => onView(commission.id)}>
                        <VisibilityIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                      <ActionIcon title="Edit" onClick={() => onEdit(commission.id)}>
                        <EditIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                      <ActionIcon title="Clone" onClick={() => onClone(commission.id)}>
                        <ContentCopyIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                      <ActionIcon title="Delete" onClick={() => onDelete(commission.id)} danger>
                        <DeleteIcon sx={{ fontSize: 15 }} />
                      </ActionIcon>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 10, borderBottom: 'none' }}>
                  <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        border: `1px solid ${INK.hairline}`,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: INK.subtle,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 22 }} />
                    </Box>
                    <Typography sx={{ fontFamily: FONTS.serif, fontSize: 28, color: INK.text, letterSpacing: '-0.01em' }}>
                      No commissions on record.
                    </Typography>
                    <Typography sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, maxWidth: 320 }}>
                      Create a new commission, or adjust filters to surface existing entries.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: `1px solid ${INK.hairline}`,
          fontFamily: FONTS.sans,
          color: INK.muted,
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
            fontFamily: FONTS.mono,
            fontSize: 11.5,
            letterSpacing: '0.04em',
            color: INK.muted,
          },
          '& .MuiTablePagination-toolbar': { paddingLeft: 3, paddingRight: 3, minHeight: 48 },
          '& .MuiIconButton-root': { color: INK.muted, '&:hover': { color: INK.accent, bgcolor: INK.accentSoft } },
        }}
      />
    </>
  );
};

/* ─────────────────────────────────────────────
   Small primitives — action icon, shared styles
   ───────────────────────────────────────────── */
const ActionIcon: React.FC<{
  title: string;
  onClick: () => void;
  children: React.ReactNode;
  danger?: boolean;
}> = ({ title, onClick, children, danger }) => (
  <Tooltip title={title}>
    <IconButton
      size="small"
      onClick={onClick}
      sx={{
        width: 28,
        height: 28,
        borderRadius: 0.5,
        color: INK.muted,
        '&:hover': {
          bgcolor: danger ? '#FBEAE6' : INK.accentSoft,
          color: danger ? INK.inactive : INK.accent,
        },
      }}
    >
      {children}
    </IconButton>
  </Tooltip>
);

export default CommissionsPage;
